import { useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import type { TableProps } from 'antd';
import {
  ArrowLeftOutlined,
  BookOutlined,
  CalendarOutlined,
  DeleteOutlined,
  DownloadOutlined,
  DragOutlined,
  EditOutlined,
  FileOutlined,
  FolderAddOutlined,
  FolderFilled,
  FolderOpenOutlined,
  HomeOutlined,
  ReloadOutlined,
  SafetyOutlined,
  SearchOutlined,
  SwapOutlined,
  SyncOutlined,
  TeamOutlined,
  UploadOutlined,
} from '@ant-design/icons';

import { DataTable, MenuThaoTac, PageHeader } from '../../../components/shared';
import type { MucThaoTac } from '../../../components/shared';
import { Button, Empty, Input, Select, Skeleton } from '../../../components/ui';
import { useDieuHuong } from '../../../hooks/useDuongDan';
import { useLoi } from '../../../hooks/useLoi';
import { useModulePermissions } from '../../../hooks/usePermission';
import { useT } from '../../../i18n';
import { KHONG_CO, dungLuongVN, ngayVN, soVN } from '../../../lib/dinh-dang';
import { maLoi } from '../../../lib/errors';
import { HopXacNhanThaoTac } from '../../nguoi-dung/components/HopXacNhanThaoTac';
import { CanhBaoTrongHop } from '../../tenant/components/phan-form';
import { FormHieuLuc } from '../components/FormHieuLuc';
import { FormKho } from '../components/FormKho';
import { FormTaiTep } from '../components/FormTaiTep';
import { FormThuMuc } from '../components/FormThuMuc';
import { HopChonThuMuc, HopChuyenBo } from '../components/HopChuyenTep';
import { HopNguoiDuocDung, HopPhongBanTep } from '../components/HopPhanQuyen';
import { OHieuLuc, OPhanQuyen, OTrangThaiNap } from '../components/o-tep';
import { LOAI_TEP, QUYEN, THU_TU_LOAI_TEP } from '../constants';
import {
  useChiTietKho,
  useDanhSachTep,
  useDongBoLai,
  useTaiXuong,
  useThuMuc,
  useXoaTep,
  useXoaThuMuc,
} from '../hooks/useTriThuc';
import { chuoiThuMuc, conCua, dongBoLaiDuoc, duongDanThuMuc, dungCay } from '../nghiep-vu/tri-thuc';
import type { Folder, KnowledgeBase, KnowledgeFile, LoaiTep } from '../types';

type Dong = { loai: 'thuMuc'; id: string; thuMuc: Folder } | { loai: 'tep'; id: string; tep: KnowledgeFile };

type HopMo =
  | { loai: 'suaKho' }
  | { loai: 'taiTep' }
  | { loai: 'taoThuMuc' }
  | { loai: 'doiTen'; thuMuc: Folder }
  | { loai: 'quyenThuMuc'; thuMuc: Folder }
  | { loai: 'xoaThuMuc'; thuMuc: Folder }
  | { loai: 'hieuLuc'; tep: KnowledgeFile }
  | { loai: 'chonThuMuc'; tep: KnowledgeFile }
  | { loai: 'chuyenBo'; tep: KnowledgeFile }
  | { loai: 'nguoiTep'; tep: KnowledgeFile }
  | { loai: 'phongBanTep'; tep: KnowledgeFile }
  | { loai: 'xoaTep'; tep: KnowledgeFile }
  | null;

/**
 * Chi tiết một bộ tri thức (`/tri-thuc/kho/:id`): duyệt thư mục như trình quản lý tệp, tải tệp lên, đặt
 * hiệu lực, phân quyền, theo dõi trạng thái nạp của hàng đợi index trong module.
 *
 * Không lọc thì bảng là nội dung của thư mục đang mở: thư mục con trước, tệp sau. Có tìm kiếm hay lọc
 * loại thì tìm trong cả bộ, mỗi tệp kèm đường dẫn thư mục của nó. Hộp xem chunk của native chưa có ở đây
 * — chờ module index có API đọc chunk.
 */
export function ChiTietKhoView() {
  const { id = '' } = useParams<{ id: string }>();
  const { t } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const kho = useChiTietKho(id);

  const tieuDeNhom = t('nav.muc.quanLyTriThuc');
  const veDanhSach = () => dieuHuong('/tri-thuc');

  if (kho.isLoading || !kho.data) {
    const khongThay = maLoi(kho.error) === 'NOT_FOUND' || kho.error?.status === 404;
    return (
      <div className="flex h-full flex-col bg-subtle">
        <div className="flex-1 space-y-4 overflow-y-auto p-4">
          <PageHeader
            breadcrumb={[{ label: t('nav.nhom.triThucAi') }, { label: tieuDeNhom, onClick: veDanhSach }]}
            titleIcon={<BookOutlined className="text-primary-600" />}
            title={kho.isLoading ? <Skeleton className="h-6 w-48" /> : t('triThuc.chiTiet.khongDoc')}
            actions={
              <Button variant="outline" size="md" icon={<ArrowLeftOutlined />} onClick={veDanhSach}>
                {t('chung.quayLai')}
              </Button>
            }
          />
          {kho.isLoading ? (
            <Skeleton className="h-64 rounded-lg" />
          ) : khongThay ? (
            <div className="rounded-lg border border-default bg-surface shadow-sm">
              <Empty icon={BookOutlined} title={t('triThuc.chiTiet.khongThay')} description={t('triThuc.chiTiet.khongThayMo')} />
            </div>
          ) : (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-default bg-surface px-4 py-3 shadow-sm">
              <span className="min-w-0 text-sm text-danger-700">{t('triThuc.chiTiet.loi', { loi: dichLoi(kho.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void kho.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          )}
        </div>
      </div>
    );
  }

  // `key`: đổi bộ (bấm sang bộ khác) là dựng lại từ đầu, không mang thư mục đang mở của bộ cũ sang.
  return <NoiDungKho key={kho.data.id} kho={kho.data} lamMoiKho={() => void kho.refetch()} dangTaiKho={kho.isFetching} />;
}

function NoiDungKho({ kho, lamMoiKho, dangTaiKho }: { kho: KnowledgeBase; lamMoiKho: () => void; dangTaiKho: boolean }) {
  const { t, locale } = useT();
  const dichLoi = useLoi();
  const dieuHuong = useDieuHuong();
  const quyen = useModulePermissions(QUYEN);

  const [thuMucMo, setThuMucMo] = useState<string | null>(null);
  const [nhap, setNhap] = useState('');
  const [timKiem, setTimKiem] = useState('');
  const [loai, setLoai] = useState<LoaiTep | undefined>();
  const [hop, setHop] = useState<HopMo>(null);

  const coLoc = Boolean(timKiem || loai);
  const thuMucQ = useThuMuc(kho.id);
  const tep = useDanhSachTep(kho.id, coLoc ? undefined : (thuMucMo ?? 'root'), timKiem, loai);
  const dongBo = useDongBoLai();
  const taiXuong = useTaiXuong();
  const xoaTep = useXoaTep();
  const xoaThuMuc = useXoaThuMuc(kho.id);

  const dsThuMuc = useMemo(() => thuMucQ.data?.items ?? [], [thuMucQ.data]);
  const cay = useMemo(() => dungCay(dsThuMuc, (a, b) => a.localeCompare(b, locale)), [dsThuMuc, locale]);
  const duongDan = thuMucMo ? chuoiThuMuc(dsThuMuc, thuMucMo) : [];
  const tenThuMucMo = duongDan.at(-1)?.name ?? null;

  const dong: Dong[] = useMemo(() => {
    const s = timKiem.toLocaleLowerCase(locale);
    const thuMuc = !coLoc
      ? conCua(cay, thuMucMo).map((n) => n.thuMuc)
      : timKiem && !loai
        ? dsThuMuc.filter((f) => f.name.toLocaleLowerCase(locale).includes(s))
        : [];
    return [
      ...thuMuc.map((f): Dong => ({ loai: 'thuMuc', id: `thu-muc-${f.id}`, thuMuc: f })),
      ...tep.dong.map((f): Dong => ({ loai: 'tep', id: f.id, tep: f })),
    ];
  }, [cay, coLoc, dsThuMuc, loai, locale, tep.dong, thuMucMo, timKiem]);

  const moThuMuc = (fid: string | null) => {
    setThuMucMo(fid);
    setNhap('');
    setTimKiem('');
    setLoai(undefined);
  };

  const xoaLoc = () => {
    setNhap('');
    setTimKiem('');
    setLoai(undefined);
  };

  const lamMoi = () => {
    lamMoiKho();
    void thuMucQ.refetch();
    void tep.refetch();
  };

  const loiTaiDau = tep.error && tep.dong.length === 0 && !tep.isFetchNextPageError;
  const dangTaiDau = tep.isLoading || (!coLoc && thuMucQ.isLoading);

  const mucThuMuc = (f: Folder): MucThaoTac[] => [
    { khoa: 'mo', nhan: t('triThuc.thuMuc.mo'), icon: <FolderOpenOutlined />, nhom: 'xem', onChon: () => moThuMuc(f.id) },
    { khoa: 'doi-ten', nhan: t('triThuc.thuMuc.doiTen'), icon: <EditOutlined />, hien: quyen.thuMucSua,
      onChon: () => setHop({ loai: 'doiTen', thuMuc: f }) },
    { khoa: 'quyen', nhan: t('triThuc.quyen.nguoiDuocDung'), icon: <SafetyOutlined />, hien: quyen.thuMucChiaSe,
      onChon: () => setHop({ loai: 'quyenThuMuc', thuMuc: f }) },
    { khoa: 'xoa', nhan: t('triThuc.thuMuc.xoa'), icon: <DeleteOutlined />, nhom: 'pha-huy', hien: quyen.thuMucXoa,
      onChon: () => setHop({ loai: 'xoaThuMuc', thuMuc: f }) },
  ];

  const mucTep = (f: KnowledgeFile): MucThaoTac[] => [
    { khoa: 'tai-xuong', nhan: t('triThuc.tep.taiXuong'), icon: <DownloadOutlined />, nhom: 'xem', hien: quyen.tepDoc && f.has_file,
      voHieu: taiXuong.isPending && taiXuong.variables?.id === f.id,
      onChon: () => taiXuong.mutate({ loai: 'tep', id: f.id, ten: f.file_name }) },
    { khoa: 'dong-bo', nhan: t('triThuc.tep.dongBoLai'), moTa: t('triThuc.tep.dongBoLaiMo'), icon: <SyncOutlined />,
      hien: quyen.tepSua && dongBoLaiDuoc(f), voHieu: dongBo.isPending && dongBo.variables?.id === f.id,
      onChon: () => dongBo.mutate(f) },
    { khoa: 'hieu-luc', nhan: t('triThuc.tep.suaHieuLuc'), icon: <CalendarOutlined />, hien: quyen.tepSua,
      onChon: () => setHop({ loai: 'hieuLuc', tep: f }) },
    { khoa: 'thu-muc', nhan: t('triThuc.tep.chuyenThuMuc'), icon: <DragOutlined />, hien: quyen.tepSua && Boolean(thuMucQ.data),
      onChon: () => setHop({ loai: 'chonThuMuc', tep: f }) },
    { khoa: 'chuyen-bo', nhan: t('triThuc.tep.chuyenBo'), icon: <SwapOutlined />, hien: quyen.tepSua,
      onChon: () => setHop({ loai: 'chuyenBo', tep: f }) },
    { khoa: 'nguoi', nhan: t('triThuc.quyen.nguoiDuocDoc'), icon: <SafetyOutlined />, hien: quyen.tepChiaSe,
      onChon: () => setHop({ loai: 'nguoiTep', tep: f }) },
    { khoa: 'phong-ban', nhan: t('triThuc.quyen.phongBanDuocDoc'), icon: <TeamOutlined />, hien: quyen.tepChiaSe,
      onChon: () => setHop({ loai: 'phongBanTep', tep: f }) },
    { khoa: 'xoa', nhan: t('triThuc.tep.xoa'), icon: <DeleteOutlined />, nhom: 'pha-huy', hien: quyen.tepXoa,
      onChon: () => setHop({ loai: 'xoaTep', tep: f }) },
  ];

  const columns: TableProps<Dong>['columns'] = [
    {
      title: t('triThuc.cot.ten'),
      width: 320,
      render: (_v, row) =>
        row.loai === 'thuMuc' ? (
          <span className="flex min-w-0 max-w-80 items-center gap-2">
            <FolderFilled aria-hidden className="shrink-0 text-primary-500" />
            <button
              type="button"
              className="truncate text-left text-sm font-medium text-content-primary hover:text-primary-700 hover:underline"
              onClick={() => moThuMuc(row.thuMuc.id)}
            >
              {row.thuMuc.name}
            </button>
          </span>
        ) : (
          <span className="flex min-w-0 max-w-80 items-start gap-2">
            <FileOutlined aria-hidden className="mt-0.5 shrink-0 text-content-muted" />
            <span className="flex min-w-0 flex-col">
              <span className="truncate text-sm text-content-primary" title={row.tep.file_name}>
                {row.tep.file_name}
              </span>
              {coLoc && (
                <span className="truncate text-2xs text-content-muted">
                  {row.tep.folder_id
                    ? duongDanThuMuc(dsThuMuc, row.tep.folder_id).join(' / ') || KHONG_CO
                    : t('triThuc.thuMuc.gocBo')}
                </span>
              )}
            </span>
          </span>
        ),
    },
    {
      title: t('triThuc.cot.loai'),
      width: 110,
      render: (_v, row) => (
        <span className="whitespace-nowrap text-xs text-content-secondary">
          {row.loai === 'thuMuc' ? t('triThuc.thuMuc.loai') : t(LOAI_TEP[row.tep.file_type])}
        </span>
      ),
    },
    {
      title: t('triThuc.cot.kichThuoc'),
      width: 100,
      render: (_v, row) => (
        <span className="whitespace-nowrap text-xs tabular-nums text-content-secondary">
          {row.loai === 'tep' && row.tep.has_file ? dungLuongVN(row.tep.file_size_bytes, locale) : KHONG_CO}
        </span>
      ),
    },
    {
      title: t('triThuc.cot.ngayTai'),
      width: 110,
      render: (_v, row) => (
        <span className="whitespace-nowrap text-xs text-content-muted">
          {ngayVN(row.loai === 'thuMuc' ? row.thuMuc.created_at : row.tep.uploaded_at)}
        </span>
      ),
    },
    {
      title: t('triThuc.cot.hieuLuc'),
      width: 180,
      render: (_v, row) => (row.loai === 'tep' ? <OHieuLuc tep={row.tep} /> : <span className="text-xs text-content-muted">{KHONG_CO}</span>),
    },
    {
      title: t('triThuc.cot.trangThaiNap'),
      width: 190,
      render: (_v, row) => (row.loai === 'tep' ? <OTrangThaiNap tep={row.tep} /> : <span className="text-xs text-content-muted">{KHONG_CO}</span>),
    },
    {
      title: t('triThuc.cot.phanQuyen'),
      width: 170,
      render: (_v, row) =>
        row.loai === 'tep' ? (
          <OPhanQuyen tep={row.tep} />
        ) : (
          <span className="text-xs text-content-muted">
            {t(row.thuMuc.permissions_configured ? 'triThuc.quyen.thuMucDaDat' : 'triThuc.quyen.thuMucChuaDat')}
          </span>
        ),
    },
    {
      title: t('chung.thaoTac'),
      width: 72,
      align: 'center',
      fixed: 'right',
      render: (_v, row) =>
        row.loai === 'thuMuc' ? (
          <MenuThaoTac tenBanGhi={row.thuMuc.name} muc={mucThuMuc(row.thuMuc)} />
        ) : (
          <MenuThaoTac tenBanGhi={row.tep.file_name} muc={mucTep(row.tep)} />
        ),
    },
  ];

  const nutTaiLen = quyen.tepTao ? (
    <Button variant="primary" size="md" icon={<UploadOutlined />} onClick={() => setHop({ loai: 'taiTep' })}>
      {t('triThuc.tep.taiLenTieuDe')}
    </Button>
  ) : undefined;

  return (
    <div className="flex h-full flex-col bg-subtle">
      <div className="flex-1 space-y-4 overflow-y-auto p-4">
        <PageHeader
          breadcrumb={[
            { label: t('nav.nhom.triThucAi') },
            { label: t('nav.muc.quanLyTriThuc'), onClick: () => dieuHuong('/tri-thuc') },
            { label: kho.name },
          ]}
          titleIcon={<BookOutlined className="text-primary-600" />}
          title={kho.name}
          subtitle={t('triThuc.chiTiet.phuDe', { ma: kho.code, phongBan: kho.managing_department.name ?? KHONG_CO })}
          actions={
            <>
              <Button
                variant="ghost"
                size="md"
                title={t('chung.lamMoi')}
                aria-label={t('chung.lamMoi')}
                onClick={lamMoi}
              >
                <ReloadOutlined className={tep.isFetching || thuMucQ.isFetching || dangTaiKho ? 'animate-spin' : ''} />
              </Button>
              {quyen.khoSua && (
                <Button variant="outline" size="md" icon={<EditOutlined />} onClick={() => setHop({ loai: 'suaKho' })}>
                  {t('triThuc.kho.suaBo')}
                </Button>
              )}
              {nutTaiLen}
            </>
          }
        />

        {kho.collection_error && (
          <CanhBaoTrongHop>{t('triThuc.chiTiet.chuaNoiIndex', { loi: kho.collection_error })}</CanhBaoTrongHop>
        )}

        <section
          aria-label={t('triThuc.chiTiet.noiDung')}
          className="overflow-hidden rounded-lg border border-default bg-surface shadow-sm"
        >
          <div className="space-y-3 border-b border-default p-4">
            <div className="flex flex-wrap items-center gap-2.5">
              <Input
                allowClear
                className="max-w-xs"
                prefix={<SearchOutlined className="text-content-muted" />}
                placeholder={t('triThuc.chiTiet.timKiem')}
                aria-label={t('triThuc.chiTiet.timKiem')}
                value={nhap}
                onChange={(e) => {
                  setNhap(e.target.value);
                  if (!e.target.value) setTimKiem('');
                }}
                onPressEnter={() => setTimKiem(nhap.trim())}
                onBlur={() => setTimKiem(nhap.trim())}
              />
              <Select<LoaiTep>
                allowClear
                className="min-w-[10rem]"
                placeholder={t('triThuc.chiTiet.moiLoai')}
                aria-label={t('triThuc.cot.loai')}
                options={THU_TU_LOAI_TEP.map((l) => ({ value: l, label: t(LOAI_TEP[l]) }))}
                value={loai}
                onChange={setLoai}
              />
              <div className="flex-1" />
              {quyen.thuMucTao && !coLoc && (
                <Button variant="outline" size="md" icon={<FolderAddOutlined />} onClick={() => setHop({ loai: 'taoThuMuc' })}>
                  {t('triThuc.thuMuc.taoTieuDe')}
                </Button>
              )}
            </div>

            {coLoc ? (
              <div className="flex flex-wrap items-center gap-2 text-sm text-content-secondary">
                <span>{t('triThuc.chiTiet.dangTimCaBo')}</span>
                <Button variant="link" size="sm" onClick={xoaLoc}>
                  {t('chung.xoaBoLoc')}
                </Button>
              </div>
            ) : (
              <nav aria-label={t('triThuc.chiTiet.duongDan')} className="flex flex-wrap items-center gap-1 text-sm">
                <DuongDanMuc dangMo={thuMucMo === null} onClick={() => moThuMuc(null)}>
                  <HomeOutlined aria-hidden />
                  {t('triThuc.thuMuc.gocBo')}
                </DuongDanMuc>
                {duongDan.map((f, i) => (
                  <span key={f.id} className="flex min-w-0 items-center gap-1">
                    <span aria-hidden className="text-content-muted">›</span>
                    <DuongDanMuc dangMo={i === duongDan.length - 1} onClick={() => moThuMuc(f.id)}>
                      <span className="max-w-48 truncate">{f.name}</span>
                    </DuongDanMuc>
                  </span>
                ))}
              </nav>
            )}

            {thuMucQ.isError && (
              <div role="alert" className="flex flex-wrap items-center justify-between gap-3">
                <span className="min-w-0 text-xs text-danger-700">{t('triThuc.thuMuc.loiDoc', { loi: dichLoi(thuMucQ.error) })}</span>
                <Button variant="outline" size="sm" onClick={() => void thuMucQ.refetch()}>
                  {t('chung.thuLai')}
                </Button>
              </div>
            )}
          </div>

          {loiTaiDau ? (
            <div role="alert" className="flex flex-wrap items-center justify-between gap-3 px-4 py-3">
              <span className="min-w-0 text-sm text-danger-700">{t('triThuc.tep.loiDanhSach', { loi: dichLoi(tep.error) })}</span>
              <Button variant="outline" size="sm" onClick={() => void tep.refetch()}>
                {t('chung.thuLai')}
              </Button>
            </div>
          ) : (
            <>
              <DataTable<Dong>
                caption={t('triThuc.chiTiet.bangCaption', { ten: tenThuMucMo ?? kho.name })}
                dataSource={dong}
                columns={columns}
                loading={dangTaiDau}
                size="middle"
                pagination={false}
                locale={{
                  emptyText: dangTaiDau ? (
                    ' '
                  ) : coLoc ? (
                    // Rỗng vì bộ lọc: bỏ mô tả và lời mời hành động.
                    <Empty icon={SearchOutlined} title={t('triThuc.chiTiet.rongLoc')} />
                  ) : (
                    <Empty
                      icon={FolderOpenOutlined}
                      title={t(thuMucMo ? 'triThuc.chiTiet.thuMucTrong' : 'triThuc.chiTiet.boTrong')}
                      description={quyen.tepTao ? t('triThuc.chiTiet.trongMo') : undefined}
                      action={nutTaiLen}
                    />
                  ),
                }}
              />
              {tep.dong.length > 0 && (tep.hasNextPage || tep.isFetchNextPageError) && (
                <div className="flex flex-wrap items-center justify-between gap-3 border-t border-default px-4 py-3">
                  <span className="text-xs text-content-muted">
                    {t('triThuc.tep.dangHienCoThem', { so: soVN(tep.dong.length, locale) })}
                  </span>
                  {tep.isFetchNextPageError && (
                    <span role="alert" className="min-w-0 flex-1 text-xs text-danger-700">
                      {t('triThuc.loiTaiThem', { loi: dichLoi(tep.error) })}
                    </span>
                  )}
                  <Button variant="outline" size="sm" loading={tep.isFetchingNextPage} onClick={() => void tep.fetchNextPage()}>
                    {tep.isFetchNextPageError ? t('chung.thuLai') : t('chung.taiThem')}
                  </Button>
                </div>
              )}
            </>
          )}
        </section>
      </div>

      {hop?.loai === 'suaKho' && <FormKho kho={kho} open onClose={() => setHop(null)} />}
      {hop?.loai === 'taiTep' && (
        <FormTaiTep kbId={kho.id} thuMuc={thuMucMo} tenThuMuc={tenThuMucMo} open onClose={() => setHop(null)} />
      )}
      {(hop?.loai === 'taoThuMuc' || hop?.loai === 'doiTen') && (
        <FormThuMuc
          kbId={kho.id}
          cha={thuMucMo}
          tenCha={tenThuMucMo}
          thuMuc={hop.loai === 'doiTen' ? hop.thuMuc : undefined}
          open
          onClose={() => setHop(null)}
        />
      )}
      {hop?.loai === 'quyenThuMuc' && (
        <HopNguoiDuocDung kbId={kho.id} doiTuong={{ loai: 'thuMuc', thuMuc: hop.thuMuc }} open onClose={() => setHop(null)} />
      )}
      {hop?.loai === 'nguoiTep' && (
        <HopNguoiDuocDung kbId={kho.id} doiTuong={{ loai: 'tep', tep: hop.tep }} open onClose={() => setHop(null)} />
      )}
      {hop?.loai === 'phongBanTep' && <HopPhongBanTep tep={hop.tep} open onClose={() => setHop(null)} />}
      {hop?.loai === 'hieuLuc' && <FormHieuLuc tep={hop.tep} open onClose={() => setHop(null)} />}
      {hop?.loai === 'chonThuMuc' && <HopChonThuMuc tep={hop.tep} thuMuc={dsThuMuc} open onClose={() => setHop(null)} />}
      {hop?.loai === 'chuyenBo' && <HopChuyenBo tep={hop.tep} open onClose={() => setHop(null)} />}
      {hop?.loai === 'xoaThuMuc' && (
        <HopXacNhanThaoTac
          open
          tieuDe={t('triThuc.thuMuc.xoaTieuDe')}
          than={t('triThuc.thuMuc.xoaThan', { ten: hop.thuMuc.name })}
          nhanXacNhan={t('triThuc.thuMuc.xoa')}
          nguyHiem
          dangChay={xoaThuMuc.isPending}
          loi={xoaThuMuc.error ? dichLoi(xoaThuMuc.error) : null}
          onClose={() => {
            xoaThuMuc.reset();
            setHop(null);
          }}
          onXacNhan={() =>
            xoaThuMuc.mutate(hop.thuMuc, {
              onSuccess: () => {
                // Đang đứng trong thư mục vừa xoá (hay con của nó): về gốc bộ.
                if (thuMucMo && duongDan.some((f) => f.id === hop.thuMuc.id)) setThuMucMo(null);
                setHop(null);
              },
            })
          }
        />
      )}
      {hop?.loai === 'xoaTep' && (
        <HopXacNhanThaoTac
          open
          tieuDe={t('triThuc.tep.xoaTieuDe')}
          than={t('triThuc.tep.xoaThan', { ten: hop.tep.file_name })}
          nhanXacNhan={t('triThuc.tep.xoa')}
          nguyHiem
          dangChay={xoaTep.isPending}
          loi={xoaTep.error ? dichLoi(xoaTep.error) : null}
          onClose={() => {
            xoaTep.reset();
            setHop(null);
          }}
          onXacNhan={() => xoaTep.mutate(hop.tep, { onSuccess: () => setHop(null) })}
        />
      )}
    </div>
  );
}

function DuongDanMuc({ dangMo, onClick, children }: { dangMo: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-current={dangMo ? 'location' : undefined}
      className={
        dangMo
          ? 'flex min-w-0 items-center gap-1 rounded px-2 py-1 font-medium text-content-primary'
          : 'flex min-w-0 items-center gap-1 rounded px-2 py-1 text-content-muted transition-colors hover:bg-subtle hover:text-primary-700'
      }
    >
      {children}
    </button>
  );
}
